import EventEmitter from 'events'

import React from 'react'
import PropTypes from 'prop-types'

import app from 'common/builder/components/chart/FlowChart/app'
import { STATES } from 'common/builder/components/chart/FlowChart/config'
import ConnectorPoint from 'common/builder/components/chart/FlowChart/nodes/shared/ConnectorPoint'

// Round to nearest even number
const getWidth = () => 2 * Math.round(app.getConnectorWidth() / 2)

class Position extends EventEmitter {
  constructor() {
    super()
    this.x1 = 0
    this.y1 = 0
    this.x2 = 0
    this.y2 = 0
    this.width = getWidth()
    this.arrowVisible = false
  }
}

export default function withPosition(BaseComponent) {
  return class PositionProvider extends React.PureComponent {
    static propTypes = {
      sourceId: PropTypes.string.isRequired,
      targetId: PropTypes.string,
    }

    state = {
      isCreating: false,
    }

    elements = {
      source: null,
      target: null,
      contextMenu: null,
    }

    position = new Position()

    componentDidMount() {
      app.on('state', this.handleAppStateChange)
      app.on('willRender', this.updatePosition)
      this.updatePosition()
    }

    componentDidUpdate(prevProps, prevState) {
      if (
        prevProps.sourceId !== this.props.sourceId ||
        prevProps.targetId !== this.props.targetId
      ) {
        this.elements.source = null
        this.elements.target = null
        this.updatePosition()
      }
    }

    componentWillUnmount() {
      app.removeListener('state', this.handleAppStateChange)
      app.removeListener('willRender', this.updatePosition)
    }

    getElements = () => {
      const { sourceId, targetId } = this.props
      const { elements } = this
      if (!elements.source) {
        elements.source = ConnectorPoint.getSourcePoint(sourceId)
      }
      if (!elements.target) {
        elements.target = ConnectorPoint.getTargetPoint(targetId)
      }
      if (!elements.contextMenu) {
        elements.contextMenu = app.getNode('$CreateNodeMenu$')
      }
      return elements
    }

    updatePosition = () => {
      let { x1, y1, x2, y2 } = this.position
      const { source, target, contextMenu } = this.getElements()
      let hasSource = false
      let hasTarget = false

      if (source) {
        const { x, y } = source.getAbsolutePosition()
        x1 = x
        y1 = y
        hasSource = true
      }

      if (target) {
        const { x, y } = target.getAbsolutePosition()
        x2 = x
        y2 = y
        hasTarget = true
      }

      if (this.state.isCreating) {
        const { x, y } = app.getNode('$pointer$').getConnectorsEnd()
        x2 = x
        y2 = y
        hasTarget = true
      }

      if (contextMenu && contextMenu.sourceId === this.props.sourceId) {
        const { x, y } = contextMenu.getConnectorsEnd()
        x2 = x
        y2 = y
        hasTarget = true
      }

      const width = getWidth()
      const arrowVisible = hasSource && hasTarget

      if (
        x1 !== this.position.x1 ||
        y1 !== this.position.y1 ||
        x2 !== this.position.x2 ||
        y2 !== this.position.y2 ||
        width !== this.position.width ||
        arrowVisible !== this.position.arrowVisible
      ) {
        this.position.x1 = x1
        this.position.y1 = y1
        this.position.x2 = x2
        this.position.y2 = y2
        this.position.width = width
        this.position.arrowVisible = arrowVisible
        this.position.emit('update')
      }
    }

    handleAppStateChange = (state) => {
      const isCreating =
        state === STATES.CREATING_NEW_CONNECTOR && app.stateOptions.src === this.props.sourceId
      if (isCreating !== this.state.isCreating) {
        this.setState({ isCreating })
      }
    }

    render() {
      const { isCreating } = this.state

      return <BaseComponent {...this.props} position={this.position} isCreating={isCreating} />
    }
  }
}
